import React, { ReactNode, useCallback, useMemo } from 'react';
import { S2Checkbox } from 'apps/sigma2-ui/src/components/ui/Checkbox';
import { WorkflowWriteBackMode } from 'libs/end-to-end/src/types/databases/workflows/workflow-write-back-mode';
import { ConfigureWriteBackMode } from 'apps/sigma2-ui/src/components/workflows/ConfigureWriteBackMode';
import { WriteBackToLeverStrategy } from 'libs/end-to-end/src/types/databases/workflows/strategies/lever-write-back';
import { WorkflowCandidateResumeScoreWriteBackDetails } from 'libs/end-to-end/src/types/databases/workflows/candidate-resume-score-write-back/workflow-candidate-resume-score-write-back';
import { WorkflowCandidateResumeScoreWriteBackScoreSourceTypesDropdowns } from 'libs/end-to-end/src/types/databases/workflows/candidate-resume-score-write-back/workflow-candidate-resume-score-write-back';
import { WorkflowCandidateResumeScoreWriteBackV2Config } from 'libs/end-to-end/src/types/databases/workflows/candidate-resume-score-write-back/workflow-candidate-resume-score-write-back';
import { ConfigureWorkflowBaseProps } from 'apps/sigma2-ui/src/components/workflows/types';
import { ConfigureWriteBackLeverStrategy } from 'apps/sigma2-ui/src/components/workflows/ConfigureWriteBackLeverStrategy';
import { ConfigureCandidateResumeScoreWriteBackV2Config } from 'apps/sigma2-ui/src/components/workflows/ConfigureCandidateResumeScoreWriteBackV2Config';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { SelectBoxWithSearch } from 'apps/sigma2-ui/src/components/ui/SelectBoxWithSearch';
import { DataSetSelector } from 'apps/sigma2-ui/src/data-wizard/components/DataSetSelector';

export interface WorkflowCandidateResumeScoreWriteBackDetailsProps
  extends ConfigureWorkflowBaseProps {
  details: WorkflowCandidateResumeScoreWriteBackDetails | null;
  updateDetails: (
    details: WorkflowCandidateResumeScoreWriteBackDetails | null,
  ) => void;
}

export const ConfigureCandidateResumeScoreWriteBackDetails = ({
  details,
  updateDetails,
  listDataSetsPattern,
  listSecretsPattern,
  organizationId,
}: WorkflowCandidateResumeScoreWriteBackDetailsProps) => {
  const updateDetailsPartial = useCallback(
    (partial: Partial<WorkflowCandidateResumeScoreWriteBackDetails>) => {
      if (!details) {
        updateDetails(null);
        return;
      }
      updateDetails({
        ...details,
        ...partial,
      });
    },
    [details, updateDetails],
  );

  const toggleWriteBackEnabled = useCallback(() => {
    updateDetails(
      details
        ? null
        : {
            type: 'candidateResumeScoreWriteBack',
            strategy: {
              type: 'lever',
            },
            scoreSource: {
              type: 'transform',
            },
          },
    );
  }, [details, updateDetails]);

  const updateMode = useCallback(
    (mode: WorkflowWriteBackMode | null) => {
      updateDetailsPartial({
        mode: mode ?? undefined,
      });
    },
    [updateDetailsPartial],
  );

  const updateStrategy = useCallback(
    (strategy: WriteBackToLeverStrategy | null) => {
      updateDetailsPartial({
        strategy: strategy ?? undefined,
      });
    },
    [updateDetailsPartial],
  );

  const updateNumberOfSamplesToSend = useCallback(
    (numberOfSamplesToSend: number | undefined) => {
      updateDetailsPartial({
        numberOfSamplesToSend:
          numberOfSamplesToSend !== undefined && isNaN(numberOfSamplesToSend)
            ? undefined
            : numberOfSamplesToSend,
      });
    },
    [updateDetailsPartial],
  );

  const updateNumPreviousDaysOfApplicants = useCallback(
    (numPreviousDaysOfApplicants: number | undefined) => {
      updateDetailsPartial({
        numPreviousDaysOfApplicants:
          numPreviousDaysOfApplicants !== undefined &&
          isNaN(numPreviousDaysOfApplicants)
            ? undefined
            : numPreviousDaysOfApplicants,
      });
    },
    [updateDetailsPartial],
  );

  const updateV2Config = useCallback(
    (v2Config: WorkflowCandidateResumeScoreWriteBackV2Config | null) => {
      updateDetailsPartial({
        v2Config: v2Config ?? undefined,
      });
    },
    [updateDetailsPartial],
  );

  const sourceUi = useMemo(() => {
    if (!details) {
      return;
    }

    let sourceDetailsUi: ReactNode | null;
    switch (details.scoreSource.type) {
      case 'transform':
        sourceDetailsUi = null;
        break;
      case 'composite-resume-grader-data-set':
        sourceDetailsUi = (
          <DataSetSelector
            dataSetId={details.scoreSource.dataSetId}
            setDataSetId={(dataSetId: string) => {
              updateDetailsPartial({
                scoreSource: {
                  type: 'composite-resume-grader-data-set',
                  dataSetId,
                },
              });
            }}
            dataSets={
              listDataSetsPattern.status === 'done'
                ? listDataSetsPattern.entity.dataSets
                : []
            }
            label={'Select a data set'}
          />
        );
    }

    return (
      <Spacing size={'md'} direction={'vertical'}>
        <SelectBoxWithSearch
          type={'single'}
          setSelected={(value) => {
            switch (value) {
              case 'transform':
                updateDetailsPartial({
                  scoreSource: {
                    type: 'transform',
                  },
                });
                break;
              case 'composite-resume-grader-data-set':
                updateDetailsPartial({
                  scoreSource: {
                    type: 'composite-resume-grader-data-set',
                    dataSetId: '',
                  },
                });
                break;
            }
          }}
          selected={details.scoreSource.type}
          list={WorkflowCandidateResumeScoreWriteBackScoreSourceTypesDropdowns}
          label={null}
        />

        {sourceDetailsUi}
      </Spacing>
    );
  }, [details, updateDetailsPartial, listDataSetsPattern]);

  return (
    <div className="space-y-6 p-6 bg-white rounded-lg shadow-md">
      <h2 className="text-xl font-semibold">
        Configure Candidate Resume Score Write Back
      </h2>
      <S2Checkbox checked={Boolean(details)} onChange={toggleWriteBackEnabled}>
        Enable the candidate resume score write back
      </S2Checkbox>

      {details && (
        <>
          <ConfigureWriteBackMode
            mode={details.mode ?? null}
            updateMode={updateMode}
          />
          {details.strategy?.type === 'lever' && (
            <ConfigureWriteBackLeverStrategy
              strategy={details.strategy ?? null}
              updateStrategy={updateStrategy}
              listSecretsPattern={listSecretsPattern}
              organizationId={organizationId}
              listDataSetsPattern={listDataSetsPattern}
            />
          )}
          <div className="space-y-4">
            <label className="space-y-1 flex flex-col">
              <span>Number of Samples To Send</span>
              <input
                type="number"
                value={details.numberOfSamplesToSend ?? ''}
                onChange={(e) => {
                  updateNumberOfSamplesToSend(e.target.valueAsNumber);
                }}
                className="form-input border rounded p-2"
              />
            </label>

            <label className="space-y-1 flex flex-col">
              <span>Number of Previous Days of Applicants</span>
              <input
                type="number"
                value={details.numPreviousDaysOfApplicants ?? ''}
                onChange={(e) => {
                  updateNumPreviousDaysOfApplicants(e.target.valueAsNumber);
                }}
                className="form-input border rounded p-2"
              />
              <p className="text-sm text-gray-500">
                Specify how many days back to look for applicants to process.
                Leave empty to process all applicants.
              </p>
            </label>

            <div className="text-sm font-semibold">Score Source</div>
            {sourceUi}

            <S2Checkbox
              checked={details?.explanationsEnabled ?? false}
              onChange={() =>
                updateDetailsPartial({
                  explanationsEnabled: details?.explanationsEnabled
                    ? undefined
                    : true,
                })
              }
            >
              Include Score Explanation in writeback note
            </S2Checkbox>
          </div>

          <ConfigureCandidateResumeScoreWriteBackV2Config
            v2Config={details.v2Config ?? null}
            updateV2Config={updateV2Config}
            listSecretsPattern={listSecretsPattern}
            organizationId={organizationId}
            listDataSetsPattern={listDataSetsPattern}
            legacyStrategy={details.strategy}
            legacyMode={details.mode}
          />
        </>
      )}
    </div>
  );
};
